
import React, { useState, useEffect, useRef } from 'react';
import './SystemMonitorPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Card from '../../components/shared/Card';
import Badge from '../../components/shared/Badge';
import Button from '../../components/shared/Button';
import { useToast } from '../../context/ToastContext';

interface Metric {
  cpu: number;
  memory: number;
  disk: number;
  network: number;
  timestamp: number;
}

const SERVICES = [
  { name: 'API Server', status: 'online', uptime: '99.98%', latency: '23ms', port: 3001 },
  { name: 'Database (Postgres)', status: 'online', uptime: '99.99%', latency: '4ms', port: 5432 },
  { name: 'Redis Cache', status: 'online', uptime: '100%', latency: '1ms', port: 6379 },
  { name: 'AI Inference', status: 'warning', uptime: '98.4%', latency: '340ms', port: 8080 },
  { name: 'WebSocket Server', status: 'online', uptime: '99.95%', latency: '8ms', port: 3002 },
  { name: 'Backup Worker', status: 'offline', uptime: '94.2%', latency: '--', port: 9000 },
];

const ALERTS = [
  { id: 1, level: 'warn', msg: 'AI Inference latency spike: 340ms (threshold: 300ms)', time: '2m ago' },
  { id: 2, level: 'info', msg: 'Scheduled backup completed successfully', time: '18m ago' },
  { id: 3, level: 'error', msg: 'Backup Worker process exited unexpectedly (code 1)', time: '32m ago' },
  { id: 4, level: 'info', msg: 'SSL certificate renewed — valid for 90 days', time: '2h ago' },
];

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const w = 80, h = 28;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - 2 - ((v - min) / range) * (h - 4);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GaugeRing({ value, color, label }: { value: number; color: string; label: string }) {
  const r = 42, cx = 50, cy = 50;
  const circumference = 2 * Math.PI * r;
  const dash = (value / 100) * circumference;
  return (
    <div className="gauge-ring">
      <svg viewBox="0 0 100 100" width="96" height="96">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dasharray 0.5s ease', filter: `drop-shadow(0 0 6px ${color}66)` }}
        />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="16" fontWeight="800" fill="#E5E7EB">{value}%</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="8" fill="#94A3B8">{label}</text>
      </svg>
    </div>
  );
}

export default function SystemMonitorPage() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [history, setHistory] = useState<Metric[]>([]);
  const [paused, setPaused] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    const generate = (): Metric => ({
      cpu: Math.round(25 + Math.random() * 55),
      memory: Math.round(40 + Math.random() * 35),
      disk: 73,
      network: Math.round(Math.random() * 100),
      timestamp: Date.now(),
    });

    setHistory(Array.from({ length: 20 }, generate));

    if (paused) return;
    const interval = setInterval(() => {
      const m = generate();
      setMetrics(prev => {
        const next = [...prev.slice(-4), m];
        setHistory(h => [...h.slice(-39), m]);
        return next;
      });
    }, 1500);
    return () => clearInterval(interval);
  }, [paused]);

  const current = history[history.length - 1] || { cpu: 48, memory: 62, disk: 73, network: 34, timestamp: Date.now() };

  return (
    <div className="sysmon-page">
      <PageHeader
        title="System Monitor"
        breadcrumb={['Ops', 'Monitor']}
        actions={
          <>
            <Badge variant={paused ? 'warning' : 'success'}>{paused ? 'Paused' : 'Live'}</Badge>
            <Button variant="ghost" size="sm" onClick={() => setPaused(p => !p)}>
              {paused ? '▶ Resume' : '⏸ Pause'}
            </Button>
            <Button size="sm" onClick={() => addToast('info', 'Report exported!')}>Export</Button>
          </>
        }
      />

      {/* Gauge Row */}
      <div className="sysmon-gauges">
        <Card className="sysmon-gauge-card">
          <GaugeRing value={current.cpu} color="#1282D6" label="CPU" />
          <div className="gauge-info">
            <div className="gauge-title">CPU Usage</div>
            <MiniSparkline data={history.slice(-12).map(m => m.cpu)} color="#1282D6" />
            <div className="gauge-sub">{current.cpu < 50 ? 'Normal' : current.cpu < 80 ? 'Elevated' : 'Critical'}</div>
          </div>
        </Card>
        <Card className="sysmon-gauge-card">
          <GaugeRing value={current.memory} color="#8B5CF6" label="RAM" />
          <div className="gauge-info">
            <div className="gauge-title">Memory</div>
            <MiniSparkline data={history.slice(-12).map(m => m.memory)} color="#8B5CF6" />
            <div className="gauge-sub">{Math.round(current.memory * 0.16 * 10) / 10} / 16 GB</div>
          </div>
        </Card>
        <Card className="sysmon-gauge-card">
          <GaugeRing value={current.disk} color="#F59E0B" label="Disk" />
          <div className="gauge-info">
            <div className="gauge-title">Disk I/O</div>
            <MiniSparkline data={history.slice(-12).map(m => m.disk)} color="#F59E0B" />
            <div className="gauge-sub">730 GB / 1 TB</div>
          </div>
        </Card>
        <Card className="sysmon-gauge-card">
          <GaugeRing value={current.network} color="#22C55E" label="Net" />
          <div className="gauge-info">
            <div className="gauge-title">Network</div>
            <MiniSparkline data={history.slice(-12).map(m => m.network)} color="#22C55E" />
            <div className="gauge-sub">{current.network} Mb/s</div>
          </div>
        </Card>
      </div>

      {/* Services + Alerts */}
      <div className="sysmon-row2">
        <Card title="Services" actions={<Badge variant="success">{SERVICES.filter(s => s.status === 'online').length} Online</Badge>}>
          <div className="services-list">
            {SERVICES.map(s => (
              <div key={s.name} className="service-row">
                <div className={`service-dot service-dot--${s.status}`} />
                <div className="service-info">
                  <span className="service-name">{s.name}</span>
                  <span className="service-port">:{s.port}</span>
                </div>
                <div className="service-stats">
                  <span className="service-latency">{s.latency}</span>
                  <span className="service-uptime">{s.uptime}</span>
                </div>
                <Badge variant={s.status === 'online' ? 'success' : s.status === 'warning' ? 'warning' : 'danger'}>
                  {s.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Alerts & Logs" actions={<button className="card-icon-btn" onClick={() => addToast('info', 'Alerts cleared')}>Clear</button>}>
          <div className="alerts-list">
            {ALERTS.map(a => (
              <div key={a.id} className={`alert-item alert-item--${a.level}`}>
                <span className="alert-icon">{a.level === 'error' ? '✕' : a.level === 'warn' ? '⚠' : 'ℹ'}</span>
                <div className="alert-content">
                  <span className="alert-msg">{a.msg}</span>
                  <span className="alert-time">{a.time}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
